import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { 
  MessageCircle, QrCode, Phone, User, Users, Send, 
  RefreshCw, X, Check, CheckCheck, Clock, Image,
  UserPlus, Link as LinkIcon, Search, Loader2
} from 'lucide-react';
import { io } from 'socket.io-client';

function WhatsAppTab() {
  // Connection state
  const [connectionStatus, setConnectionStatus] = useState('checking');
  const [qrCode, setQrCode] = useState(null);
  const [connectedInfo, setConnectedInfo] = useState(null);
  
  // Chat state
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createType, setCreateType] = useState('lead');
  
  // Leads for linking
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState('');
  
  // WebSocket
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Fetch connection status
  const checkStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/status`);
      const status = response.data.status;
      setConnectionStatus(status);
      
      if (status === 'connected') {
        setConnectedInfo(response.data.info);
        setQrCode(null);
        fetchChats();
      } else if (status === 'qr_ready') {
        fetchQR();
      }
    } catch (error) {
      console.error('Status check error:', error);
      setConnectionStatus('error');
    }
  }, []);

  // Fetch QR code
  const fetchQR = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/qr`);
      setQrCode(response.data.qrCode);
    } catch (error) {
      console.error('QR fetch error:', error);
    }
  };

  // Connect WhatsApp
  const connectWhatsApp = async () => {
    setConnectionStatus('connecting');
    setMessage({ type: '', text: '' });
    
    try {
      await axios.post(`${API}/whatsapp/connect`);
      setConnectionStatus('waiting_qr');
      
      // Poll for QR code
      const pollQR = setInterval(async () => {
        const statusRes = await axios.get(`${API}/whatsapp/status`);
        if (statusRes.data.status === 'qr_ready') {
          const qrRes = await axios.get(`${API}/whatsapp/qr`);
          setQrCode(qrRes.data.qrCode);
          setConnectionStatus('qr_ready');
          clearInterval(pollQR);
        } else if (statusRes.data.status === 'connected') {
          setConnectedInfo(statusRes.data.info);
          setConnectionStatus('connected');
          clearInterval(pollQR);
          fetchChats();
        }
      }, 2000);
      
      // Stop polling after 2 minutes
      setTimeout(() => clearInterval(pollQR), 120000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to connect' });
      setConnectionStatus('error');
    }
  };

  // Disconnect WhatsApp
  const disconnectWhatsApp = async () => {
    try {
      await axios.post(`${API}/whatsapp/disconnect`);
      setConnectionStatus('disconnected');
      setConnectedInfo(null);
      setChats([]);
      setSelectedChat(null);
      setMessages([]);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to disconnect' });
    }
  };

  // Fetch chats
  const fetchChats = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/chats?limit=50`);
      setChats(response.data.chats || []);
    } catch (error) {
      console.error('Fetch chats error:', error);
    }
  };

  // Fetch messages for selected chat
  const fetchMessages = async (chatId) => {
    try {
      const response = await axios.get(`${API}/whatsapp/messages/${encodeURIComponent(chatId)}?limit=100`);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error('Fetch messages error:', error);
    }
  };

  // Send message
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChat) return;
    
    setSending(true);
    try {
      const phone = selectedChat.id.replace('@c.us', '').replace('@g.us', '');
      await axios.post(`${API}/whatsapp/send`, {
        to: phone,
        message: newMessage,
        lead_id: selectedChat.linkedLeadId
      });
      
      // Add message optimistically
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        body: newMessage,
        fromMe: true,
        timestamp: Math.floor(Date.now() / 1000),
        ack: 1
      }]);
      
      setNewMessage('');
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to send message' });
    } finally {
      setSending(false);
    }
  };

  // Create lead from chat
  const createLeadFromChat = async () => {
    if (!selectedChat) return;
    
    const phone = selectedChat.contact?.number || selectedChat.id.replace('@c.us', '');
    const name = selectedChat.name || selectedChat.contact?.pushname || phone;
    
    try {
      const response = await axios.post(`${API}/whatsapp/create-lead-from-chat`, {
        phone,
        name,
        chat_id: selectedChat.id
      });
      
      setMessage({ type: 'success', text: response.data.message });
      setShowCreateModal(false);
      fetchChats();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to create lead' });
    }
  };

  // Create contact from chat
  const createContactFromChat = async () => {
    if (!selectedChat) return;
    
    const phone = selectedChat.contact?.number || selectedChat.id.replace('@c.us', '');
    const name = selectedChat.name || selectedChat.contact?.pushname || phone;
    
    try {
      const response = await axios.post(`${API}/whatsapp/create-contact-from-chat`, {
        phone,
        name,
        chat_id: selectedChat.id,
        lead_id: selectedLead || null
      });
      
      setMessage({ type: 'success', text: response.data.message });
      setShowCreateModal(false);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to create contact' });
    }
  };

  // Link chat to existing lead
  const linkChatToLead = async () => {
    if (!selectedChat || !selectedLead) return;
    
    try {
      await axios.post(`${API}/whatsapp/link-chat-to-lead`, {
        lead_id: selectedLead,
        chat_id: selectedChat.id,
        phone: selectedChat.contact?.number
      });
      
      setMessage({ type: 'success', text: 'Chat linked to lead' });
      setShowCreateModal(false);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to link chat' });
    }
  };

  // Fetch leads for linking
  const fetchLeads = async () => {
    try {
      const response = await axios.get(`${API}/leads?limit=100`);
      setLeads(response.data || []);
    } catch (error) {
      console.error('Fetch leads error:', error);
    }
  };

  // Setup WebSocket connection
  useEffect(() => {
    const setupWebSocket = async () => {
      try {
        const response = await axios.get(`${API}/whatsapp/websocket-url`);
        const { url, userId } = response.data;
        
        const socket = io(url, { transports: ['websocket'] });
        socketRef.current = socket;
        
        socket.on('connect', () => {
          socket.emit('join', userId);
        });
        
        socket.on('qr', (data) => {
          setQrCode(data.qrCode);
          setConnectionStatus('qr_ready');
        });
        
        socket.on('ready', (data) => {
          setConnectionStatus('connected');
          setConnectedInfo(data.info);
          setQrCode(null);
          fetchChats();
        });
        
        socket.on('authenticated', () => {
          setConnectionStatus('authenticated');
        });
        
        socket.on('disconnected', () => {
          setConnectionStatus('disconnected');
          setConnectedInfo(null);
        });
        
        socket.on('message', async (msg) => {
          // Store incoming message
          try {
            await axios.post(`${API}/whatsapp/store-incoming`, msg);
          } catch (e) {
            console.error('Store message error:', e);
          }
          
          // Update UI if this chat is selected
          if (selectedChat && msg.chat?.id === selectedChat.id) {
            setMessages(prev => [...prev, {
              id: msg.id,
              body: msg.body,
              fromMe: false,
              timestamp: msg.timestamp,
              contact: msg.contact
            }]);
          }
          
          // Refresh chat list
          fetchChats();
        });
        
        socket.on('message_ack', (data) => {
          setMessages(prev => prev.map(m => 
            m.id === data.id ? { ...m, ack: data.ack } : m
          ));
        });
        
      } catch (error) {
        console.error('WebSocket setup error:', error);
      }
    };
    
    setupWebSocket();
    
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChat]);

  // Initial load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await checkStatus();
      await fetchLeads();
      setLoading(false);
    };
    init();
  }, [checkStatus]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Select chat handler
  const handleSelectChat = (chat) => {
    setSelectedChat(chat);
    fetchMessages(chat.id);
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };

  // Get message status icon
  const getAckIcon = (ack) => {
    switch (ack) {
      case 0: return <Clock className="w-3 h-3 text-slate-400" />;
      case 1: return <Check className="w-3 h-3 text-slate-400" />;
      case 2: return <CheckCheck className="w-3 h-3 text-slate-400" />;
      case 3: return <CheckCheck className="w-3 h-3 text-blue-500" />;
      default: return null;
    }
  };

  // Filter chats
  const filteredChats = chats.filter(chat => 
    (chat.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (chat.contact?.number || '').includes(searchQuery)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  // Not connected view
  if (connectionStatus !== 'connected') {
    return (
      <div className="space-y-6" data-testid="whatsapp-connect">
        {message.text && (
          <div className={`px-4 py-3 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
            {message.text}
          </div>
        )}

        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageCircle className="w-8 h-8 text-green-600" />
          </div>
          
          <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">Connect WhatsApp</h3>
          <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
            Connect your personal WhatsApp account to send messages, create leads, and manage conversations directly from the CRM.
          </p>

          {connectionStatus === 'qr_ready' && qrCode ? (
            <div className="space-y-4">
              <div className="bg-white dark:bg-slate-800 p-4 rounded-xl inline-block border-2 border-green-200">
                <img src={qrCode} alt="WhatsApp QR Code" className="w-64 h-64" />
              </div>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Open WhatsApp on your phone → Menu → Linked Devices → Link a Device
              </p>
              <button
                onClick={() => checkStatus()}
                className="text-green-600 hover:text-green-700 text-sm flex items-center gap-2 mx-auto"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh Status
              </button>
            </div>
          ) : (
            <button
              onClick={connectWhatsApp}
              disabled={connectionStatus === 'connecting'}
              className="px-6 py-3 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-lg hover:bg-slate-800 dark:hover:bg-white disabled:opacity-50 flex items-center gap-2 mx-auto"
            >
              {connectionStatus === 'connecting' ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <QrCode className="w-5 h-5" />
                  Connect WhatsApp
                </>
              )}
            </button>
          )}

          <div className="mt-8 p-4 bg-yellow-50 rounded-lg text-left max-w-lg mx-auto">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> This uses WhatsApp Web automation. Your account may be at risk of being banned if used for spam. Use responsibly for genuine business conversations only.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Connected view - Chat interface
  return (
    <div className="h-[calc(100vh-200px)] flex bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid="whatsapp-connected">
      {/* Sidebar - Chat List */}
      <div className="w-80 border-r border-slate-200 dark:border-slate-700 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900 dark:text-white">{connectedInfo?.pushname}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">+{connectedInfo?.phone}</p>
              </div>
            </div>
            <button
              onClick={disconnectWhatsApp}
              className="text-red-500 hover:text-red-600 text-xs"
              title="Disconnect"
            >
              Disconnect
            </button>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search chats..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
            />
          </div>
        </div>

        {/* Chat List */}
        <div className="flex-1 overflow-y-auto">
          {filteredChats.length === 0 ? (
            <div className="p-4 text-center text-slate-500 dark:text-slate-400">
              <p>No chats yet</p>
              <button
                onClick={fetchChats}
                className="mt-2 text-green-600 hover:text-green-700 text-sm flex items-center gap-1 mx-auto"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          ) : (
            filteredChats.map((chat) => (
              <div
                key={chat.id}
                onClick={() => handleSelectChat(chat)}
                className={`p-3 border-b border-slate-100 dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900 transition ${
                  selectedChat?.id === chat.id ? 'bg-green-50' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
                    {chat.isGroup ? (
                      <Users className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                    ) : (
                      <User className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-slate-900 dark:text-white truncate text-sm">{chat.name}</p>
                      <span className="text-xs text-slate-400">{formatDate(chat.timestamp)}</span>
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                      {chat.lastMessage?.body || 'No messages'}
                    </p>
                  </div>
                  {chat.unreadCount > 0 && (
                    <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {chat.unreadCount}
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {selectedChat ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                  {selectedChat.isGroup ? (
                    <Users className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                  ) : (
                    <User className="w-5 h-5 text-slate-500 dark:text-slate-400" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">{selectedChat.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {selectedChat.contact?.number ? `+${selectedChat.contact.number}` : ''}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setShowCreateModal(true); setCreateType('lead'); }}
                  className="px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 flex items-center gap-1"
                >
                  <UserPlus className="w-4 h-4" />
                  Create Lead
                </button>
                <button
                  onClick={() => { setShowCreateModal(true); setCreateType('link'); }}
                  className="px-3 py-1.5 text-sm bg-slate-100 text-slate-600 dark:text-slate-400 rounded-lg hover:bg-slate-200 flex items-center gap-1"
                >
                  <LinkIcon className="w-4 h-4" />
                  Link
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#e5ddd5]">
              {messages.map((msg, idx) => (
                <div
                  key={msg.id || idx}
                  className={`flex ${msg.fromMe ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] px-3 py-2 rounded-lg shadow-sm ${
                      msg.fromMe 
                        ? 'bg-[#dcf8c6] rounded-br-none' 
                        : 'bg-white dark:bg-slate-800 rounded-bl-none'
                    }`}
                  >
                    {!msg.fromMe && msg.contact?.name && (
                      <p className="text-xs font-medium text-green-600 mb-1">{msg.contact.name}</p>
                    )}
                    <p className="text-sm text-slate-800 dark:text-slate-100 whitespace-pre-wrap">{msg.body}</p>
                    <div className="flex items-center justify-end gap-1 mt-1">
                      <span className="text-xs text-slate-500 dark:text-slate-400">{formatTime(msg.timestamp)}</span>
                      {msg.fromMe && getAckIcon(msg.ack)}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <form onSubmit={sendMessage} className="p-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-full focus:outline-none focus:border-green-500"
                  disabled={sending}
                />
                <button
                  type="submit"
                  disabled={sending || !newMessage.trim()}
                  className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sending ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-slate-50 dark:bg-slate-900">
            <div className="text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-slate-500 dark:text-slate-400">Select a chat to start messaging</p>
            </div>
          </div>
        )}
      </div>

      {/* Create/Link Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {createType === 'lead' ? 'Create Lead from Chat' :
                 createType === 'contact' ? 'Create Contact from Chat' :
                 'Link Chat to Lead'}
              </h3>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 dark:text-slate-400">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Chat Info */}
              <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  <strong>Contact:</strong> {selectedChat?.name || 'Unknown'}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  <strong>Phone:</strong> +{selectedChat?.contact?.number || selectedChat?.id?.replace('@c.us', '')}
                </p>
              </div>

              {/* Type Selection */}
              <div className="flex gap-2">
                <button
                  onClick={() => setCreateType('lead')}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
                    createType === 'lead' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 dark:text-slate-400'
                  }`}
                >
                  New Lead
                </button>
                <button
                  onClick={() => setCreateType('contact')}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
                    createType === 'contact' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 dark:text-slate-400'
                  }`}
                >
                  New Contact
                </button>
                <button
                  onClick={() => setCreateType('link')}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
                    createType === 'link' ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 dark:text-slate-400'
                  }`}
                >
                  Link Existing
                </button>
              </div>

              {/* Lead selector for contact/link */}
              {(createType === 'contact' || createType === 'link') && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {createType === 'link' ? 'Select Lead to Link' : 'Associate with Lead (optional)'}
                  </label>
                  <select
                    value={selectedLead}
                    onChange={(e) => setSelectedLead(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg"
                  >
                    <option value="">Select a lead...</option>
                    {leads.map(lead => (
                      <option key={lead.id} value={lead.id}>
                        {lead.name} {lead.phone ? `(${lead.phone})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (createType === 'lead') createLeadFromChat();
                  else if (createType === 'contact') createContactFromChat();
                  else linkChatToLead();
                }}
                disabled={createType === 'link' && !selectedLead}
                className="px-4 py-2 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-lg hover:bg-slate-800 dark:hover:bg-white disabled:opacity-50"
              >
                {createType === 'lead' ? 'Create Lead' :
                 createType === 'contact' ? 'Create Contact' :
                 'Link Chat'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Toast */}
      {message.text && (
        <div className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg ${
          message.type === 'success' ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900' : 'bg-red-600 text-white'
        }`}>
          {message.text}
          <button onClick={() => setMessage({ type: '', text: '' })} className="ml-3">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

export default WhatsAppTab;
